import { getUserOrganizations } from '@/lib/firebase/queries/organizations';
import { getGlobalCustomerPermissions } from '@/lib/firebase/queries/permissions';
import type { Organization } from '@/types/organization';

/**
 * The reads every customer page waits on before it renders (route guard:
 * organizations → permissions), started as soon as Firebase knows the user —
 * alongside the users/{uid} read instead of after it — and shared by every
 * useOrganization() instance instead of one read each.
 *
 * Nothing is rendered from a cache across page loads: this only removes
 * waiting and duplicate reads within one page session, so the guards decide
 * from the same fresh data as before.
 */

// Long enough to cover one page open (prefetch → guard → page widgets).
const ORGS_TTL_MS = 10_000;
let customerOrgs: { uid: string; at: number; value: Promise<Organization[]> } | null = null;

/** The user's customer organizations, one read shared for a short while. */
export function sharedCustomerOrganizations(uid: string): Promise<Organization[]> {
  const now = Date.now();
  if (customerOrgs && customerOrgs.uid === uid && now - customerOrgs.at < ORGS_TTL_MS) {
    return customerOrgs.value;
  }
  const entry = { uid, at: now, value: getUserOrganizations(uid, 'customer') };
  customerOrgs = entry;
  // A failed read is not kept.
  entry.value.catch(() => {
    if (customerOrgs === entry) customerOrgs = null;
  });
  return entry.value;
}

/** Forget the shared organizations (explicit refresh, sign-out). */
export function clearSharedCustomerOrganizations(): void {
  customerOrgs = null;
}

/** Start the guard's reads for `uid` now; callers pick them up when they ask. */
export function prefetchCustomerSession(uid: string): void {
  sharedCustomerOrganizations(uid).catch(() => {});
  getGlobalCustomerPermissions().catch(() => {});
}
